import React, { useRef } from 'react';
import { DemoSection, SectionNavigator } from './SectionNavigator';

const pageStyle: React.CSSProperties = { marginBottom: '20px', paddingRight: '20px' };
const titleStyle: React.CSSProperties = { marginBottom: '1.5rem' };
const separatorStyle: React.CSSProperties = { border: 0, borderTop: '1px solid #e0e0e0', margin: '3rem 0' };
const sectionTitleStyle: React.CSSProperties = { marginBottom: '1rem' };
const exampleStyle: React.CSSProperties = { marginBottom: '1.5rem' };
const exampleLabelStyle: React.CSSProperties = { marginBottom: '0.5rem' };

/**
 * A demo page: the title, then its sections with a light gray separator between each one. Pass `sections` (matching
 * the ids of the DemoPageSections) to also show the "Jump to section" dropdown.
 */
export const DemoPage: React.FC<{ title: string; sections?: DemoSection[]; children: React.ReactNode }> = ({
    title,
    sections,
    children,
}) => {
    const blocks = React.Children.toArray(children);
    const topRef = useRef<HTMLDivElement>(null);

    return (
        <div
            ref={topRef}
            style={pageStyle}
        >
            <h2 style={titleStyle}>{title}</h2>
            {blocks.map((block, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <hr style={separatorStyle} />}
                    {block}
                </React.Fragment>
            ))}
            {sections && sections.length > 0 && (
                <SectionNavigator
                    sections={sections}
                    topRef={topRef}
                />
            )}
        </div>
    );
};

/** One section of a demo page, with an optional heading. `id` is the anchor the section navigator jumps to. */
export const DemoPageSection: React.FC<{ id?: string; title?: React.ReactNode; children: React.ReactNode }> = ({
    id,
    title,
    children,
}) => (
    <div id={id}>
        {title && <h5 style={sectionTitleStyle}>{title}</h5>}
        {children}
    </div>
);

/** Stacks children vertically with an even gap (for small controls such as icon buttons). */
export const DemoStack: React.FC<{ gap?: string; children: React.ReactNode }> = ({ gap = '0.75rem', children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap }}>{children}</div>
);

/** One example within a section: an optional label above the control, with consistent spacing below. */
export const DemoExample: React.FC<{ label?: React.ReactNode; children: React.ReactNode }> = ({ label, children }) => (
    <div style={exampleStyle}>
        {label && <div style={exampleLabelStyle}>{label}</div>}
        {children}
    </div>
);
